import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import type {
  ConfigHistoryEntry,
  LoanProductConfig,
  LoanProductConfigUpdatePayload,
  LoanProductHistoryEntry,
  MicroLoanConfig,
  MicroLoanConfigUpdatePayload,
} from "../api/endpoints/system_config"
import {
  getConfigChangeHistory,
  getMicroLoanConfig,
  getLoanProductConfigHistory,
  listLoanProductConfig,
  listSystemConfig,
  updateLoanProductConfig,
  updateMicroLoanConfig,
  updateSystemConfig,
} from "../api/endpoints/system_config"
import {
  AlertCard,
  Button,
  FormField,
  Input,
  Modal,
  PageSection,
  Select,
  StatusPill,
  Textarea,
} from "../components/ui"
import { formatCurrency, formatDateTime } from "../utils/formatting"

interface ConfigItem {
  key: string
  value: string
  description: string | null
  updatedBy: string | null
  updatedAt: string | null
}

interface ConfigGroupDef {
  label: string
  keys: { key: string; label: string; inputType: "text" | "number" }[]
}

interface LoanProductFormState {
  name: string
  description: string
  principalMin: string
  principalMax: string
  interestRate: string
  defaultTermDays: string
  currency: string
  preDueReminderDay: string
  gracePeriodDays: string
  categoryAStartDay: string
  categoryAPenaltyRate: string
  categoryBStartDay: string
  categoryBPenaltyRate: string
  categoryCStartDay: string
  categoryCPenaltyRate: string
  categoryZStartDay: string
  maxPenaltyCapRate: string
  penaltyCompounding: "SIMPLE" | "COMPOUND"
  isActive: boolean
  reason: string
}

interface MicroLoanTierFormState {
  tier: "T1" | "T2" | "T3" | "T4" | "T5"
  name: string
  principalCeilingUsd: string
  minTenorDays: string
  maxTenorDays: string
  applicationFeeRate: string
  interestRate: string
  requiredCycles: string
}

interface MicroLoanFormState {
  enabled: boolean
  portalEnabled: boolean
  agentAppEnabled: boolean
  productId: string
  blockIfStandardOverdue: boolean
  requireEmploymentVerified: boolean
  firstLoanManualReview: boolean
  autoApproveMinCycles: string
  minClientAgeYears: string
  defaultToMaxTenor: boolean
  promotionSmsEnabled: boolean
  demotionSmsEnabled: boolean
  promotionSmsTemplate: string
  demotionSmsTemplate: string
  tiers: MicroLoanTierFormState[]
}

const CONFIG_GROUPS: ConfigGroupDef[] = [
  {
    label: "Authentication & Security",
    keys: [
      { key: "otp_expiry_seconds", label: "OTP Expiry (seconds)", inputType: "number" },
      { key: "otp_max_retries", label: "OTP Max Retries", inputType: "number" },
      { key: "otp_lockout_seconds", label: "OTP Lockout Duration (seconds)", inputType: "number" },
      { key: "session_ttl_agent", label: "Agent Session TTL (seconds)", inputType: "number" },
      { key: "session_ttl_admin", label: "Admin Session TTL (seconds)", inputType: "number" },
      { key: "session_ttl_client", label: "Client Session TTL (seconds)", inputType: "number" },
    ],
  },
  {
    label: "Collections & Workflow",
    keys: [
      { key: "ptp_to_bp_window_days", label: "PTP-to-BP Window (days)", inputType: "number" },
      {
        key: "unprocessed_time_threshold",
        label: "Unprocessed Time Threshold (seconds)",
        inputType: "number",
      },
      { key: "nok_min_category", label: "NOK Minimum Category", inputType: "text" },
      { key: "max_nok_per_client", label: "Max NOK Per Client", inputType: "number" },
      {
        key: "agent_inactive_threshold_seconds",
        label: "Agent Inactive Threshold (seconds)",
        inputType: "number",
      },
      { key: "agent_capacity_max_clients", label: "Agent Max Clients", inputType: "number" },
      {
        key: "assignment_queue_age_warning_seconds",
        label: "Queue Age Warning (seconds)",
        inputType: "number",
      },
      {
        key: "assignment_queue_age_critical_seconds",
        label: "Queue Age Critical (seconds)",
        inputType: "number",
      },
    ],
  },
  {
    label: "Ticketing & SLAs",
    keys: [
      { key: "sla_urgent_seconds", label: "Urgent SLA (seconds)", inputType: "number" },
      { key: "sla_high_seconds", label: "High SLA (seconds)", inputType: "number" },
      { key: "sla_medium_seconds", label: "Medium SLA (seconds)", inputType: "number" },
      { key: "sla_low_seconds", label: "Low SLA (seconds)", inputType: "number" },
    ],
  },
  {
    label: "Data & Reporting",
    keys: [
      { key: "base_currency", label: "Base Currency", inputType: "text" },
      {
        key: "notification_retention_days",
        label: "Notification Retention (days)",
        inputType: "number",
      },
      { key: "report_frequency", label: "Report Frequency", inputType: "text" },
      { key: "audit_retention_days", label: "Audit Retention (days)", inputType: "number" },
    ],
  },
  {
    label: "Portal & Payments",
    keys: [
      {
        key: "portal_rate_limit_per_minute",
        label: "Portal Rate Limit (/min)",
        inputType: "number",
      },
      {
        key: "portal_otp_rate_limit_per_five_minutes",
        label: "Portal OTP Rate Limit (/5min)",
        inputType: "number",
      },
      {
        key: "enabled_payment_methods",
        label: "Enabled Payment Methods (JSON)",
        inputType: "text",
      },
      { key: "innbucks_api_base_url", label: "InnBucks API Base URL", inputType: "text" },
      {
        key: "reconciliation_frequency_hours",
        label: "Reconciliation Frequency (hours)",
        inputType: "number",
      },
      {
        key: "auto_match_amount_tolerance",
        label: "Auto-match Tolerance",
        inputType: "number",
      },
      {
        key: "auto_match_date_window_days",
        label: "Auto-match Date Window (days)",
        inputType: "number",
      },
      {
        key: "unmatched_payment_expiry_hours",
        label: "Unmatched Payment Expiry (hours)",
        inputType: "number",
      },
      {
        key: "portal_payment_timeout_seconds",
        label: "Portal Payment Timeout (seconds)",
        inputType: "number",
      },
      {
        key: "biometric_reauth_enabled",
        label: "Biometric Re-auth Enabled",
        inputType: "text",
      },
      { key: "default_language", label: "Default Language", inputType: "text" },
      {
        key: "portal_overdue_label_mapping",
        label: "Portal Overdue Label Mapping (JSON)",
        inputType: "text",
      },
    ],
  },
]

const LOAN_PRODUCT_FIELD_LABELS: Record<string, string> = {
  name: "Product Name",
  description: "Description",
  principalMin: "Principal Minimum",
  principalMax: "Principal Maximum",
  interestRate: "Interest Rate",
  defaultTermDays: "Default Term",
  currency: "Currency",
  preDueReminderDay: "Pre-due Reminder Day",
  gracePeriodDays: "Grace Period",
  categoryAStartDay: "Category A Start Day",
  categoryAPenaltyRate: "Category A Penalty Rate",
  categoryBStartDay: "Category B Start Day",
  categoryBPenaltyRate: "Category B Penalty Rate",
  categoryCStartDay: "Category C Start Day",
  categoryCPenaltyRate: "Category C Penalty Rate",
  categoryZStartDay: "Category Z Start Day",
  maxPenaltyCapRate: "Penalty Cap Rate",
  penaltyCompounding: "Penalty Compounding",
  isActive: "Active Status",
}

const RATE_FIELDS = new Set([
  "interestRate",
  "categoryAPenaltyRate",
  "categoryBPenaltyRate",
  "categoryCPenaltyRate",
  "maxPenaltyCapRate",
])

const MONEY_FIELDS = new Set(["principalMin", "principalMax"])

function trimNumericString(value: string) {
  return value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1")
}

function formatPercent(value: number) {
  return `${trimNumericString((value * 100).toFixed(2))}%`
}

function formatMinorAmount(value: number) {
  return new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100)
}

function toMajorAmountInput(value: number) {
  return trimNumericString((value / 100).toFixed(2))
}

function toPercentInput(value: number) {
  return trimNumericString((value * 100).toFixed(2))
}

function toWholeNumberInput(value: number) {
  return `${value}`
}

function buildLoanProductForm(product: LoanProductConfig): LoanProductFormState {
  return {
    name: product.name,
    description: product.description ?? "",
    principalMin: toMajorAmountInput(product.principalMin),
    principalMax: toMajorAmountInput(product.principalMax),
    interestRate: toPercentInput(product.interestRate),
    defaultTermDays: toWholeNumberInput(product.defaultTermDays),
    currency: product.currency,
    preDueReminderDay: toWholeNumberInput(product.preDueReminderDay),
    gracePeriodDays: toWholeNumberInput(product.gracePeriodDays),
    categoryAStartDay: toWholeNumberInput(product.categoryAStartDay),
    categoryAPenaltyRate: toPercentInput(product.categoryAPenaltyRate),
    categoryBStartDay: toWholeNumberInput(product.categoryBStartDay),
    categoryBPenaltyRate: toPercentInput(product.categoryBPenaltyRate),
    categoryCStartDay: toWholeNumberInput(product.categoryCStartDay),
    categoryCPenaltyRate: toPercentInput(product.categoryCPenaltyRate),
    categoryZStartDay: toWholeNumberInput(product.categoryZStartDay),
    maxPenaltyCapRate: toPercentInput(product.maxPenaltyCapRate),
    penaltyCompounding: product.penaltyCompounding,
    isActive: product.isActive,
    reason: "",
  }
}

function parseRequiredNumber(rawValue: string, label: string) {
  const value = rawValue.trim()
  if (!value) {
    throw new Error(`${label} is required.`)
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a valid number.`)
  }

  return parsed
}

function parseWholeNumber(rawValue: string, label: string, minimum = 0) {
  const parsed = parseRequiredNumber(rawValue, label)
  if (!Number.isInteger(parsed)) {
    throw new Error(`${label} must be a whole number.`)
  }

  if (parsed < minimum) {
    if (minimum === 1) {
      throw new Error(`${label} must be at least 1.`)
    }
    throw new Error(`${label} cannot be negative.`)
  }

  return parsed
}

function parseMajorAmount(rawValue: string, label: string) {
  const parsed = parseRequiredNumber(rawValue, label)
  if (parsed < 0) {
    throw new Error(`${label} cannot be negative.`)
  }

  return Math.round(parsed * 100)
}

function parsePercent(rawValue: string, label: string) {
  const parsed = parseRequiredNumber(rawValue, label)
  if (parsed < 0) {
    throw new Error(`${label} cannot be negative.`)
  }

  return parsed / 100
}

function buildLoanProductPayload(form: LoanProductFormState): LoanProductConfigUpdatePayload {
  const name = form.name.trim()
  if (!name) {
    throw new Error("Product name is required.")
  }

  const currency = form.currency.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new Error("Currency must be a 3-letter ISO code.")
  }

  const reason = form.reason.trim()
  if (!reason) {
    throw new Error("Reason for change is required.")
  }

  return {
    name,
    description: form.description.trim() || null,
    principalMin: parseMajorAmount(form.principalMin, "Principal minimum"),
    principalMax: parseMajorAmount(form.principalMax, "Principal maximum"),
    interestRate: parsePercent(form.interestRate, "Interest rate"),
    defaultTermDays: parseWholeNumber(form.defaultTermDays, "Default term", 1),
    currency,
    preDueReminderDay: parseWholeNumber(form.preDueReminderDay, "Pre-due reminder day"),
    gracePeriodDays: parseWholeNumber(form.gracePeriodDays, "Grace period"),
    categoryAStartDay: parseWholeNumber(form.categoryAStartDay, "Category A start day"),
    categoryAPenaltyRate: parsePercent(form.categoryAPenaltyRate, "Category A penalty rate"),
    categoryBStartDay: parseWholeNumber(form.categoryBStartDay, "Category B start day"),
    categoryBPenaltyRate: parsePercent(form.categoryBPenaltyRate, "Category B penalty rate"),
    categoryCStartDay: parseWholeNumber(form.categoryCStartDay, "Category C start day"),
    categoryCPenaltyRate: parsePercent(form.categoryCPenaltyRate, "Category C penalty rate"),
    categoryZStartDay: parseWholeNumber(form.categoryZStartDay, "Category Z start day"),
    maxPenaltyCapRate: parsePercent(form.maxPenaltyCapRate, "Penalty cap rate"),
    penaltyCompounding: form.penaltyCompounding,
    isActive: form.isActive,
    reason,
  }
}

function renderHistoryValue(key: string, value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "-"
  }

  if (typeof value === "boolean") {
    return value ? "Active" : "Inactive"
  }

  if (RATE_FIELDS.has(key) && typeof value === "number") {
    return formatPercent(value)
  }

  if (MONEY_FIELDS.has(key) && typeof value === "number") {
    return formatMinorAmount(value)
  }

  if (key === "penaltyCompounding" && typeof value === "string") {
    return value === "COMPOUND" ? "Compound" : "Simple"
  }

  return String(value)
}

function renderHistoryChanges(
  row: ConfigHistoryEntry | LoanProductHistoryEntry,
  labelMap: Record<string, string> = {},
) {
  if (!row.beforeState || !row.afterState) {
    return "-"
  }

  const keys = Object.keys(row.afterState)
  if (keys.length === 0) {
    return "No field changes recorded"
  }

  return (
    <div className="space-y-1">
      {keys.map((key) => (
        <div key={key} className="font-mono">
          <span className="text-slate-500">{labelMap[key] ?? key}:</span>{" "}
          <span className="text-red-500 line-through">{renderHistoryValue(key, row.beforeState?.[key])}</span>{" "}
          <span className="text-green-600">{renderHistoryValue(key, row.afterState?.[key])}</span>
        </div>
      ))}
    </div>
  )
}

function buildConfirmationSummary(product: LoanProductConfig, payload: LoanProductConfigUpdatePayload) {
  return [
    {
      label: "Principal Range",
      current: `${formatCurrency(product.principalMin, product.currency)} - ${formatCurrency(product.principalMax, product.currency)}`,
      next: `${formatCurrency(payload.principalMin, payload.currency)} - ${formatCurrency(payload.principalMax, payload.currency)}`,
    },
    {
      label: "Interest & Term",
      current: `${formatPercent(product.interestRate)} • ${product.defaultTermDays} days`,
      next: `${formatPercent(payload.interestRate)} • ${payload.defaultTermDays} days`,
    },
    {
      label: "Reminder & Grace",
      current: `Day ${product.preDueReminderDay} reminder • ${product.gracePeriodDays} day grace`,
      next: `Day ${payload.preDueReminderDay} reminder • ${payload.gracePeriodDays} day grace`,
    },
    {
      label: "Penalty Ladder",
      current: `A ${product.categoryAStartDay}/${formatPercent(product.categoryAPenaltyRate)} • B ${product.categoryBStartDay}/${formatPercent(product.categoryBPenaltyRate)} • C ${product.categoryCStartDay}/${formatPercent(product.categoryCPenaltyRate)} • Z ${product.categoryZStartDay}`,
      next: `A ${payload.categoryAStartDay}/${formatPercent(payload.categoryAPenaltyRate)} • B ${payload.categoryBStartDay}/${formatPercent(payload.categoryBPenaltyRate)} • C ${payload.categoryCStartDay}/${formatPercent(payload.categoryCPenaltyRate)} • Z ${payload.categoryZStartDay}`,
    },
    {
      label: "Penalty Cap",
      current: `${formatPercent(product.maxPenaltyCapRate)} • ${product.penaltyCompounding}`,
      next: `${formatPercent(payload.maxPenaltyCapRate)} • ${payload.penaltyCompounding}`,
    },
    {
      label: "Status",
      current: product.isActive ? "Active" : "Inactive",
      next: payload.isActive ? "Active" : "Inactive",
    },
  ]
}

function buildMicroLoanForm(config: MicroLoanConfig): MicroLoanFormState {
  return {
    enabled: config.enabled,
    portalEnabled: config.portalEnabled,
    agentAppEnabled: config.agentAppEnabled,
    productId: config.productId,
    blockIfStandardOverdue: config.blockIfStandardOverdue,
    requireEmploymentVerified: config.requireEmploymentVerified,
    firstLoanManualReview: config.firstLoanManualReview,
    autoApproveMinCycles: toWholeNumberInput(config.autoApproveMinCycles),
    minClientAgeYears: toWholeNumberInput(config.minClientAgeYears),
    defaultToMaxTenor: config.defaultToMaxTenor,
    promotionSmsEnabled: config.promotionSmsEnabled,
    demotionSmsEnabled: config.demotionSmsEnabled,
    promotionSmsTemplate: config.promotionSmsTemplate,
    demotionSmsTemplate: config.demotionSmsTemplate,
    tiers: config.tiers.map((tier) => ({
      tier: tier.tier,
      name: tier.name,
      principalCeilingUsd: trimNumericString(tier.principalCeilingUsd.toFixed(2)),
      minTenorDays: toWholeNumberInput(tier.minTenorDays),
      maxTenorDays: toWholeNumberInput(tier.maxTenorDays),
      applicationFeeRate: toPercentInput(tier.applicationFeeRate),
      interestRate: toPercentInput(tier.interestRate),
      requiredCycles: toWholeNumberInput(tier.requiredCycles),
    })),
  }
}

function buildMicroLoanConfigShape(
  form: MicroLoanFormState,
): Omit<MicroLoanConfigUpdatePayload, "reason"> {
  const productId = form.productId.trim()
  if (!productId) {
    throw new Error("Micro-loan backing product is required.")
  }

  const tiers = form.tiers.map((tier, index) => {
    const name = tier.name.trim()
    if (!name) {
      throw new Error(`${tier.tier} name is required.`)
    }

    const principalCeilingUsd = parseRequiredNumber(
      tier.principalCeilingUsd,
      `${tier.tier} principal ceiling`,
    )
    if (principalCeilingUsd <= 0) {
      throw new Error(`${tier.tier} principal ceiling must be greater than zero.`)
    }

    const minTenorDays = parseWholeNumber(tier.minTenorDays, `${tier.tier} minimum tenor`, 1)
    const maxTenorDays = parseWholeNumber(tier.maxTenorDays, `${tier.tier} maximum tenor`, 1)
    if (maxTenorDays < minTenorDays) {
      throw new Error(`${tier.tier} maximum tenor must be greater than or equal to the minimum tenor.`)
    }

    if (index > 0) {
      const previousCeiling = parseRequiredNumber(
        form.tiers[index - 1].principalCeilingUsd,
        `${form.tiers[index - 1].tier} principal ceiling`,
      )
      if (principalCeilingUsd <= previousCeiling) {
        throw new Error(`${tier.tier} principal ceiling must be greater than the previous tier ceiling.`)
      }
    }

    return {
      tier: tier.tier,
      name,
      principalCeilingUsd,
      minTenorDays,
      maxTenorDays,
      applicationFeeRate: parsePercent(tier.applicationFeeRate, `${tier.tier} application fee`),
      interestRate: parsePercent(tier.interestRate, `${tier.tier} interest rate`),
      requiredCycles: parseWholeNumber(tier.requiredCycles, `${tier.tier} required cycles`),
    }
  })

  return {
    enabled: form.enabled,
    portalEnabled: form.portalEnabled,
    agentAppEnabled: form.agentAppEnabled,
    productId,
    blockIfStandardOverdue: form.blockIfStandardOverdue,
    requireEmploymentVerified: form.requireEmploymentVerified,
    firstLoanManualReview: form.firstLoanManualReview,
    autoApproveMinCycles: parseWholeNumber(
      form.autoApproveMinCycles,
      "Auto-approve minimum cycles",
    ),
    minClientAgeYears: parseWholeNumber(form.minClientAgeYears, "Minimum client age", 18),
    defaultToMaxTenor: form.defaultToMaxTenor,
    promotionSmsEnabled: form.promotionSmsEnabled,
    demotionSmsEnabled: form.demotionSmsEnabled,
    promotionSmsTemplate: form.promotionSmsTemplate.trim(),
    demotionSmsTemplate: form.demotionSmsTemplate.trim(),
    tiers,
  }
}

function buildMicroLoanPayload(
  form: MicroLoanFormState,
  reason: string,
): MicroLoanConfigUpdatePayload {
  const auditReason = reason.trim()
  if (!auditReason) {
    throw new Error("Reason for change is required.")
  }

  return {
    ...buildMicroLoanConfigShape(form),
    reason: auditReason,
  }
}

export function SystemConfigPage() {
  const queryClient = useQueryClient()
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")
  const [editReason, setEditReason] = useState("")
  const [confirmStep, setConfirmStep] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [loanProductForm, setLoanProductForm] = useState<LoanProductFormState | null>(null)
  const [loanProductConfirmStep, setLoanProductConfirmStep] = useState(false)
  const [loanProductDraft, setLoanProductDraft] = useState<LoanProductConfigUpdatePayload | null>(null)
  const [loanProductFormError, setLoanProductFormError] = useState<string | null>(null)
  const [microLoanEditOpen, setMicroLoanEditOpen] = useState(false)
  const [microLoanForm, setMicroLoanForm] = useState<MicroLoanFormState | null>(null)
  const [microLoanReason, setMicroLoanReason] = useState("")
  const [microLoanError, setMicroLoanError] = useState<string | null>(null)

  const configQuery = useQuery({
    queryKey: ["system-config"],
    queryFn: listSystemConfig,
  })

  const loanProductQuery = useQuery({
    queryKey: ["system-config-loan-products"],
    queryFn: listLoanProductConfig,
  })
  const microLoanConfigQuery = useQuery({
    queryKey: ["system-config-micro-loan"],
    queryFn: () => getMicroLoanConfig().then((response) => response.data),
  })

  const historyQuery = useQuery({
    queryKey: ["system-config-history"],
    queryFn: () => getConfigChangeHistory(50),
    enabled: showHistory,
  })

  const loanProductHistoryQuery = useQuery({
    queryKey: ["system-config-loan-products-history"],
    queryFn: () => getLoanProductConfigHistory(50),
    enabled: showHistory,
  })

  const updateMutation = useMutation({
    mutationFn: (vars: { key: string; value: string; reason: string }) =>
      updateSystemConfig([{ key: vars.key, value: vars.value }], vars.reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["system-config"] })
      await queryClient.invalidateQueries({ queryKey: ["system-config-history"] })
      closeEditModal()
    },
  })

  const loanProductUpdateMutation = useMutation({
    mutationFn: (vars: { productId: string; payload: LoanProductConfigUpdatePayload }) =>
      updateLoanProductConfig(vars.productId, vars.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["system-config-loan-products"] })
      await queryClient.invalidateQueries({ queryKey: ["system-config-loan-products-history"] })
      closeLoanProductModal()
    },
  })
  const microLoanUpdateMutation = useMutation({
    mutationFn: (payload: MicroLoanConfigUpdatePayload) => updateMicroLoanConfig(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["system-config-micro-loan"] })
      await queryClient.invalidateQueries({ queryKey: ["system-config-history"] })
      setMicroLoanEditOpen(false)
      setMicroLoanReason("")
      setMicroLoanError(null)
    },
  })

  const configs: ConfigItem[] = configQuery.data?.data ?? []
  const microLoanConfig = microLoanConfigQuery.data ?? null
  const configMap = new Map(configs.map((config) => [config.key, config]))
  const groupedKeys = new Set(CONFIG_GROUPS.flatMap((group) => group.keys.map((entry) => entry.key)))
  const uncategorizedConfigs = configs.filter((config) => !groupedKeys.has(config.key))
  const visibleGroups = [
    ...CONFIG_GROUPS,
    ...(uncategorizedConfigs.length > 0
      ? [
          {
            label: "Other Live Settings",
            keys: uncategorizedConfigs.map((config) => ({
              key: config.key,
              label: config.key.replace(/_/g, " "),
              inputType: "text" as const,
            })),
          },
        ]
      : []),
  ].filter((group) => group.keys.some((entry) => configMap.has(entry.key)))

  const loanProducts = loanProductQuery.data?.data ?? []
  const currentEditConfig = editingKey ? configMap.get(editingKey) : null
  const selectedLoanProduct =
    editingProductId && loanProductForm
      ? loanProducts.find((product) => product.id === editingProductId) ?? null
      : null

  function toggleGroup(label: string) {
    setExpandedGroups((previous) => {
      const next = new Set(previous)
      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }
      return next
    })
  }

  function openEdit(key: string) {
    const current = configMap.get(key)
    setEditingKey(key)
    setEditValue(current?.value ?? "")
    setEditReason("")
    setConfirmStep(false)
  }

  function closeEditModal() {
    setEditingKey(null)
    setEditValue("")
    setEditReason("")
    setConfirmStep(false)
  }

  function handleSave() {
    if (!confirmStep) {
      setConfirmStep(true)
      return
    }

    if (!editingKey) return
    updateMutation.mutate({ key: editingKey, value: editValue, reason: editReason })
  }

  function openLoanProductEdit(product: LoanProductConfig) {
    setEditingProductId(product.id)
    setLoanProductForm(buildLoanProductForm(product))
    setLoanProductConfirmStep(false)
    setLoanProductDraft(null)
    setLoanProductFormError(null)
  }

  function closeLoanProductModal() {
    setEditingProductId(null)
    setLoanProductForm(null)
    setLoanProductConfirmStep(false)
    setLoanProductDraft(null)
    setLoanProductFormError(null)
  }

  function updateLoanProductFormField<K extends keyof LoanProductFormState>(
    key: K,
    value: LoanProductFormState[K],
  ) {
    setLoanProductForm((current) => (current ? { ...current, [key]: value } : current))
    setLoanProductConfirmStep(false)
    setLoanProductDraft(null)
    setLoanProductFormError(null)
  }

  function handleLoanProductSave() {
    if (!editingProductId || !loanProductForm) {
      return
    }

    if (!loanProductConfirmStep || !loanProductDraft) {
      try {
        const payload = buildLoanProductPayload(loanProductForm)
        setLoanProductDraft(payload)
        setLoanProductConfirmStep(true)
        setLoanProductFormError(null)
      } catch (error) {
        setLoanProductFormError((error as Error).message)
      }
      return
    }

    loanProductUpdateMutation.mutate({
      productId: editingProductId,
      payload: loanProductDraft,
    })
  }

  function openMicroLoanModal() {
    if (!microLoanConfig) {
      return
    }

    setMicroLoanForm(buildMicroLoanForm(microLoanConfig))
    setMicroLoanReason("")
    setMicroLoanError(null)
    setMicroLoanEditOpen(true)
  }

  function updateMicroLoanFormField<K extends keyof MicroLoanFormState>(
    key: K,
    value: MicroLoanFormState[K],
  ) {
    setMicroLoanForm((current) => (current ? { ...current, [key]: value } : current))
    setMicroLoanError(null)
  }

  function updateMicroLoanTierField<K extends keyof MicroLoanTierFormState>(
    index: number,
    key: K,
    value: MicroLoanTierFormState[K],
  ) {
    setMicroLoanForm((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        tiers: current.tiers.map((tier, tierIndex) =>
          tierIndex === index ? { ...tier, [key]: value } : tier,
        ),
      }
    })
    setMicroLoanError(null)
  }

  function handleMicroLoanSave() {
    try {
      if (!microLoanForm) {
        return
      }

      microLoanUpdateMutation.mutate(buildMicroLoanPayload(microLoanForm, microLoanReason))
    } catch (error) {
      setMicroLoanError((error as Error).message)
    }
  }

  let microLoanPreviewText = ""
  let microLoanPreviewError: string | null = null
  if (microLoanForm) {
    try {
      microLoanPreviewText = JSON.stringify(buildMicroLoanConfigShape(microLoanForm), null, 2)
    } catch (error) {
      microLoanPreviewError = (error as Error).message
    }
  }

  const productSummary =
    selectedLoanProduct && loanProductDraft
      ? buildConfirmationSummary(selectedLoanProduct, loanProductDraft)
      : []

  return (
    <div className="flex flex-col gap-5">
      <AlertCard
        variant="warning"
        title="Configuration changes are live and affect all users immediately. Only Super Admins can edit these settings."
      />

      <PageSection
        title="System Configuration"
        actions={
          <Button
            variant={showHistory ? "primary" : "secondary"}
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Hide History" : "Change History"}
          </Button>
        }
      >
        {configQuery.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Loading configuration...</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {visibleGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.label)
              const groupConfigs = group.keys
                .map((item) => ({ def: item, config: configMap.get(item.key) }))
                .filter((item) => item.config)

              return (
                <div key={group.label}>
                  <button
                    className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-50"
                    onClick={() => toggleGroup(group.label)}
                    type="button"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-700">{group.label}</span>
                      <span className="text-xs text-slate-400">
                        ({groupConfigs.length} parameters)
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {isExpanded ? "\u25B2" : "\u25BC"}
                    </span>
                  </button>

                  {isExpanded ? (
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                          <th className="px-4 py-2">Parameter</th>
                          <th className="px-4 py-2">Value</th>
                          <th className="px-4 py-2">Last Updated</th>
                          <th className="w-20 px-4 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {groupConfigs.map(({ def, config }) => (
                          <tr key={def.key} className="hover:bg-slate-50">
                            <td className="px-4 py-2">
                              <div className="font-medium text-slate-700">{def.label}</div>
                              {config?.description ? (
                                <div className="mt-0.5 text-slate-400" title={config.description}>
                                  {config.description}
                                </div>
                              ) : null}
                              <div className="mt-0.5 font-mono text-slate-400">{def.key}</div>
                            </td>
                            <td className="px-4 py-2">
                              <code className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">
                                {config?.value}
                              </code>
                            </td>
                            <td className="px-4 py-2 text-slate-500">
                              {config?.updatedAt ? formatDateTime(config.updatedAt) : "-"}
                              {config?.updatedBy ? (
                                <div className="text-slate-400">{config.updatedBy}</div>
                              ) : null}
                            </td>
                            <td className="px-4 py-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openEdit(def.key)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
      </PageSection>

      <PageSection
        title="Loan Products"
        actions={<div className="text-xs text-slate-500">{loanProducts.length} products</div>}
      >
        <div className="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
                Micro loans
              </p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">
                Dedicated quick-loan configuration
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                This controls micro-loan eligibility, tier pricing, portal access, and lifecycle
                automation. The internal backing product is hidden from the generic product editor.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={!microLoanConfig}
              onClick={openMicroLoanModal}
            >
              Edit Micro Loan Settings
            </Button>
          </div>

          {microLoanConfig ? (
            <div className="mt-5 grid gap-4 xl:grid-cols-4">
              <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Availability
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  {microLoanConfig.enabled ? "Enabled" : "Disabled"} · Portal{" "}
                  {microLoanConfig.portalEnabled ? "on" : "off"}
                </p>
              </div>
              <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Manual review
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  First loan {microLoanConfig.firstLoanManualReview ? "requires" : "skips"} manual review
                </p>
              </div>
              <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Eligibility
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  Min age {microLoanConfig.minClientAgeYears} · Employment check{" "}
                  {microLoanConfig.requireEmploymentVerified ? "on" : "off"}
                </p>
              </div>
              <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Tenor default
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  {microLoanConfig.defaultToMaxTenor ? "Default to maximum tenor" : "Client selects tenor"}
                </p>
              </div>
            </div>
          ) : microLoanConfigQuery.isLoading ? (
            <p className="mt-4 text-sm text-slate-500">Loading micro-loan settings...</p>
          ) : null}

          {microLoanConfig?.tiers?.length ? (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead>
                  <tr className="border-b border-emerald-100 font-bold uppercase text-slate-500">
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Ceiling</th>
                    <th className="px-3 py-2">Tenor</th>
                    <th className="px-3 py-2">Fee</th>
                    <th className="px-3 py-2">Interest</th>
                    <th className="px-3 py-2">Cycles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/80">
                  {microLoanConfig.tiers.map((tier) => (
                    <tr key={tier.tier}>
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {tier.tier} · {tier.name}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {formatCurrency(tier.principalCeilingUsd * 100, "USD")}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {tier.minTenorDays}-{tier.maxTenorDays} days
                      </td>
                      <td className="px-3 py-2 text-slate-700">{formatPercent(tier.applicationFeeRate)}</td>
                      <td className="px-3 py-2 text-slate-700">{formatPercent(tier.interestRate)}</td>
                      <td className="px-3 py-2 text-slate-700">{tier.requiredCycles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/80 p-4 text-sm text-slate-600">
          Configure interest rates, principal ranges, due-cycle timing, and overdue penalty rules per
          loan product. These settings are Super Admin only and go live immediately after save.
        </div>

        {loanProductQuery.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Loading loan products...</p>
        ) : loanProducts.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No loan products found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Commercial Terms</th>
                  <th className="px-4 py-3">Overdue & Penalties</th>
                  <th className="px-4 py-3">Last Changed</th>
                  <th className="w-24 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loanProducts.map((product) => (
                  <tr key={product.id} className="align-top hover:bg-slate-50/70">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-800">{product.name}</div>
                        <StatusPill tone={product.isActive ? "success" : "neutral"}>
                          {product.isActive ? "Active" : "Inactive"}
                        </StatusPill>
                      </div>
                      {product.description ? (
                        <div className="mt-1 max-w-sm text-slate-500">{product.description}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      <div>
                        {formatCurrency(product.principalMin, product.currency)} -{" "}
                        {formatCurrency(product.principalMax, product.currency)}
                      </div>
                      <div className="mt-1">
                        {formatPercent(product.interestRate)} interest • {product.defaultTermDays} days •{" "}
                        {product.currency}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      <div>
                        Reminder day {product.preDueReminderDay} • Grace {product.gracePeriodDays} day
                      </div>
                      <div className="mt-1">
                        A {product.categoryAStartDay}/{formatPercent(product.categoryAPenaltyRate)} • B{" "}
                        {product.categoryBStartDay}/{formatPercent(product.categoryBPenaltyRate)} • C{" "}
                        {product.categoryCStartDay}/{formatPercent(product.categoryCPenaltyRate)} • Z{" "}
                        {product.categoryZStartDay}
                      </div>
                      <div className="mt-1">
                        Cap {formatPercent(product.maxPenaltyCapRate)} • {product.penaltyCompounding}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-500">
                      <div>{formatDateTime(product.lastChangedAt ?? product.updatedAt)}</div>
                      {product.updatedBy ? <div className="mt-1 text-slate-400">{product.updatedBy}</div> : null}
                      {product.lastChangeReason ? (
                        <div className="mt-2 max-w-xs rounded-lg bg-slate-100 px-2 py-1 text-slate-500">
                          {product.lastChangeReason}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openLoanProductEdit(product)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>

      {showHistory ? (
        <PageSection title="Change History">
          <div className="space-y-8">
            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Global Configuration History</h3>
                  <p className="text-xs text-slate-500">Live `system_config` key changes.</p>
                </div>
              </div>
              {historyQuery.isLoading ? (
                <p className="p-4 text-sm text-slate-500">Loading configuration history...</p>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Changes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(historyQuery.data?.data ?? []).map((row) => (
                      <tr key={row.auditId}>
                        <td className="px-4 py-3">{formatDateTime(row.timestamp)}</td>
                        <td className="px-4 py-3">
                          <div>{row.actorName ?? row.actorId}</div>
                          <div className="text-slate-400">{row.actorRole}</div>
                        </td>
                        <td className="px-4 py-3">{row.description ?? "-"}</td>
                        <td className="px-4 py-3">{renderHistoryChanges(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Loan Product Change History</h3>
                  <p className="text-xs text-slate-500">
                    Interest, term, overdue, and penalty changes by product.
                  </p>
                </div>
              </div>
              {loanProductHistoryQuery.isLoading ? (
                <p className="p-4 text-sm text-slate-500">Loading loan product history...</p>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Changes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(loanProductHistoryQuery.data?.data ?? []).map((row) => (
                      <tr key={row.auditId}>
                        <td className="px-4 py-3">{formatDateTime(row.timestamp)}</td>
                        <td className="px-4 py-3">
                          <div>{row.productName ?? row.productId ?? "-"}</div>
                          {row.productId ? <div className="text-slate-400">{row.productId}</div> : null}
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.actorName ?? row.actorId}</div>
                          <div className="text-slate-400">{row.actorRole}</div>
                        </td>
                        <td className="px-4 py-3">{row.description ?? "-"}</td>
                        <td className="px-4 py-3">
                          {renderHistoryChanges(row, LOAN_PRODUCT_FIELD_LABELS)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </PageSection>
      ) : null}

      <Modal
        open={editingKey !== null}
        onClose={closeEditModal}
        title={`Edit: ${editingKey ?? ""}`}
        description={currentEditConfig?.description ?? undefined}
      >
        <div className="space-y-4">
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-600">Current Value</span>
            <code className="block rounded bg-slate-100 px-3 py-2 text-sm text-slate-700">
              {currentEditConfig?.value}
            </code>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">New Value</span>
            <input
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setEditValue(event.target.value)}
              value={editValue}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Reason for Change *</span>
            <textarea
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setEditReason(event.target.value)}
              placeholder="Describe why this change is being made..."
              rows={3}
              value={editReason}
            />
          </label>

          {confirmStep ? (
            <AlertCard
              variant="caution"
              title={`You are about to change "${editingKey}" from "${currentEditConfig?.value}" to "${editValue}". This will take effect immediately.`}
            />
          ) : null}

          {updateMutation.isError ? (
            <AlertCard
              variant="danger"
              title={(updateMutation.error as Error)?.message ?? "Failed to update configuration"}
            />
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button
              variant={confirmStep ? "danger" : "primary"}
              size="sm"
              disabled={!editReason.trim() || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? "Saving..." : confirmStep ? "Confirm & Save" : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={microLoanEditOpen}
        onClose={() => setMicroLoanEditOpen(false)}
        title="Edit Micro Loan Configuration"
        description="Configure quick-loan availability, eligibility, notifications, and per-tier pricing with validation before save."
        widthClassName="max-w-6xl"
      >
        {microLoanForm ? (
          <div className="space-y-6">
            <div className="grid gap-4 xl:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Enablement</h3>
                <div className="mt-4 space-y-3">
                  <FormField label="Micro loans">
                    <Select
                      value={microLoanForm.enabled ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField("enabled", event.target.value === "true")
                      }
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Select>
                  </FormField>
                  <FormField label="Portal access">
                    <Select
                      value={microLoanForm.portalEnabled ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField("portalEnabled", event.target.value === "true")
                      }
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Select>
                  </FormField>
                  <FormField label="Agent app access">
                    <Select
                      value={microLoanForm.agentAppEnabled ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField("agentAppEnabled", event.target.value === "true")
                      }
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Select>
                  </FormField>
                  <FormField label="Backing product">
                    <Input
                      value={microLoanForm.productId}
                      onChange={(event) => updateMicroLoanFormField("productId", event.target.value)}
                    />
                  </FormField>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Eligibility & Approval</h3>
                <div className="mt-4 space-y-3">
                  <FormField label="Block if standard loan overdue">
                    <Select
                      value={microLoanForm.blockIfStandardOverdue ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "blockIfStandardOverdue",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Select>
                  </FormField>
                  <FormField label="Require verified income document">
                    <Select
                      value={microLoanForm.requireEmploymentVerified ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "requireEmploymentVerified",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Required</option>
                      <option value="false">Not required</option>
                    </Select>
                  </FormField>
                  <FormField label="First loan manual review">
                    <Select
                      value={microLoanForm.firstLoanManualReview ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "firstLoanManualReview",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Required</option>
                      <option value="false">Auto-approve allowed</option>
                    </Select>
                  </FormField>
                  <FormField label="Auto-approve minimum cycles">
                    <Input
                      inputMode="numeric"
                      value={microLoanForm.autoApproveMinCycles}
                      onChange={(event) =>
                        updateMicroLoanFormField("autoApproveMinCycles", event.target.value)
                      }
                    />
                  </FormField>
                  <FormField label="Minimum client age">
                    <Input
                      inputMode="numeric"
                      value={microLoanForm.minClientAgeYears}
                      onChange={(event) =>
                        updateMicroLoanFormField("minClientAgeYears", event.target.value)
                      }
                    />
                  </FormField>
                  <FormField label="Default tenor">
                    <Select
                      value={microLoanForm.defaultToMaxTenor ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "defaultToMaxTenor",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Default to maximum tenor</option>
                      <option value="false">Customer selects tenor</option>
                    </Select>
                  </FormField>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                <div className="mt-4 space-y-3">
                  <FormField label="Promotion SMS">
                    <Select
                      value={microLoanForm.promotionSmsEnabled ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "promotionSmsEnabled",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Select>
                  </FormField>
                  <FormField label="Demotion SMS">
                    <Select
                      value={microLoanForm.demotionSmsEnabled ? "true" : "false"}
                      onChange={(event) =>
                        updateMicroLoanFormField(
                          "demotionSmsEnabled",
                          event.target.value === "true",
                        )
                      }
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Select>
                  </FormField>
                  <FormField label="Promotion SMS template">
                    <Textarea
                      rows={4}
                      value={microLoanForm.promotionSmsTemplate}
                      onChange={(event) =>
                        updateMicroLoanFormField("promotionSmsTemplate", event.target.value)
                      }
                    />
                  </FormField>
                  <FormField label="Demotion SMS template">
                    <Textarea
                      rows={4}
                      value={microLoanForm.demotionSmsTemplate}
                      onChange={(event) =>
                        updateMicroLoanFormField("demotionSmsTemplate", event.target.value)
                      }
                    />
                  </FormField>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Tier Pricing & Tenor</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Tier ceilings must rise as customers progress. Tenor ranges and pricing are validated before save.
                </p>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                {microLoanForm.tiers.map((tier, index) => (
                  <div key={tier.tier} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {tier.tier}
                        </p>
                        <h4 className="mt-1 text-sm font-semibold text-slate-900">{tier.name || "Unnamed tier"}</h4>
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <FormField label="Display name">
                        <Input
                          value={tier.name}
                          onChange={(event) =>
                            updateMicroLoanTierField(index, "name", event.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Principal ceiling (USD)">
                        <Input
                          inputMode="decimal"
                          value={tier.principalCeilingUsd}
                          onChange={(event) =>
                            updateMicroLoanTierField(
                              index,
                              "principalCeilingUsd",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>
                      <FormField label="Minimum tenor (days)">
                        <Input
                          inputMode="numeric"
                          value={tier.minTenorDays}
                          onChange={(event) =>
                            updateMicroLoanTierField(index, "minTenorDays", event.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Maximum tenor (days)">
                        <Input
                          inputMode="numeric"
                          value={tier.maxTenorDays}
                          onChange={(event) =>
                            updateMicroLoanTierField(index, "maxTenorDays", event.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Application fee (%)">
                        <Input
                          inputMode="decimal"
                          value={tier.applicationFeeRate}
                          onChange={(event) =>
                            updateMicroLoanTierField(
                              index,
                              "applicationFeeRate",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>
                      <FormField label="Interest rate (%)">
                        <Input
                          inputMode="decimal"
                          value={tier.interestRate}
                          onChange={(event) =>
                            updateMicroLoanTierField(index, "interestRate", event.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Required cycles">
                        <Input
                          inputMode="numeric"
                          value={tier.requiredCycles}
                          onChange={(event) =>
                            updateMicroLoanTierField(index, "requiredCycles", event.target.value)
                          }
                        />
                      </FormField>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <FormField label="Reason for Change *">
                  <Textarea
                    rows={4}
                    value={microLoanReason}
                    onChange={(event) => setMicroLoanReason(event.target.value)}
                  />
                </FormField>
                <p className="mt-2 text-xs text-slate-500">
                  This note is required for audit history and should explain why the quick-loan rules are changing.
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-950 p-4 text-slate-100">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold">Raw JSON Preview</h3>
                  <span className="text-xs text-slate-400">Read only</span>
                </div>
                {microLoanPreviewError ? (
                  <p className="mt-3 text-sm text-amber-300">{microLoanPreviewError}</p>
                ) : (
                  <pre className="mt-3 max-h-[320px] overflow-auto text-xs leading-5 text-slate-200">
                    {microLoanPreviewText}
                  </pre>
                )}
              </div>
            </div>

            {microLoanError ? <AlertCard variant="danger" title={microLoanError} /> : null}
            {microLoanUpdateMutation.isError ? (
              <AlertCard
                variant="danger"
                title={(microLoanUpdateMutation.error as Error)?.message ?? "Failed to update micro-loan configuration"}
              />
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMicroLoanEditOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={microLoanUpdateMutation.isPending}
                onClick={handleMicroLoanSave}
              >
                {microLoanUpdateMutation.isPending ? "Saving..." : "Save Micro Loan Settings"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading micro-loan configuration...</p>
        )}
      </Modal>

      <Modal
        open={editingProductId !== null && loanProductForm !== null}
        onClose={closeLoanProductModal}
        title={selectedLoanProduct ? `Edit Loan Product: ${selectedLoanProduct.name}` : "Edit Loan Product"}
        description="Interest, term, overdue, and penalty changes are applied immediately after confirmation."
        widthClassName="max-w-5xl"
      >
        {loanProductForm ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Product Name</span>
                <Input
                  value={loanProductForm.name}
                  onChange={(event) => updateLoanProductFormField("name", event.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Currency</span>
                <Input
                  maxLength={3}
                  value={loanProductForm.currency}
                  onChange={(event) =>
                    updateLoanProductFormField("currency", event.target.value.toUpperCase())
                  }
                />
              </label>
              <label className="block md:col-span-2">
                <span className="mb-1 block text-xs font-medium text-slate-600">Description</span>
                <Textarea
                  rows={3}
                  value={loanProductForm.description}
                  onChange={(event) => updateLoanProductFormField("description", event.target.value)}
                />
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Principal Minimum
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.principalMin}
                  onChange={(event) =>
                    updateLoanProductFormField("principalMin", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Principal Maximum
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.principalMax}
                  onChange={(event) =>
                    updateLoanProductFormField("principalMax", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Interest Rate %</span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.interestRate}
                  onChange={(event) =>
                    updateLoanProductFormField("interestRate", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Default Term (days)
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.defaultTermDays}
                  onChange={(event) =>
                    updateLoanProductFormField("defaultTermDays", event.target.value)
                  }
                />
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Pre-due Reminder Day
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.preDueReminderDay}
                  onChange={(event) =>
                    updateLoanProductFormField("preDueReminderDay", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Grace Period (days)
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.gracePeriodDays}
                  onChange={(event) =>
                    updateLoanProductFormField("gracePeriodDays", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Status</span>
                <Select
                  value={loanProductForm.isActive ? "ACTIVE" : "INACTIVE"}
                  onChange={(event) =>
                    updateLoanProductFormField("isActive", event.target.value === "ACTIVE")
                  }
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Penalty Compounding
                </span>
                <Select
                  value={loanProductForm.penaltyCompounding}
                  onChange={(event) =>
                    updateLoanProductFormField(
                      "penaltyCompounding",
                      event.target.value as "SIMPLE" | "COMPOUND",
                    )
                  }
                >
                  <option value="SIMPLE">Simple</option>
                  <option value="COMPOUND">Compound</option>
                </Select>
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category A Start Day
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.categoryAStartDay}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryAStartDay", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category A Penalty %
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.categoryAPenaltyRate}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryAPenaltyRate", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category B Start Day
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.categoryBStartDay}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryBStartDay", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category B Penalty %
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.categoryBPenaltyRate}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryBPenaltyRate", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category C Start Day
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.categoryCStartDay}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryCStartDay", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category C Penalty %
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.categoryCPenaltyRate}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryCPenaltyRate", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Category Z Start Day
                </span>
                <Input
                  type="number"
                  step="1"
                  value={loanProductForm.categoryZStartDay}
                  onChange={(event) =>
                    updateLoanProductFormField("categoryZStartDay", event.target.value)
                  }
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Max Penalty Cap %
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={loanProductForm.maxPenaltyCapRate}
                  onChange={(event) =>
                    updateLoanProductFormField("maxPenaltyCapRate", event.target.value)
                  }
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Reason for Change *</span>
              <Textarea
                rows={3}
                placeholder="Explain why the product terms are being updated..."
                value={loanProductForm.reason}
                onChange={(event) => updateLoanProductFormField("reason", event.target.value)}
              />
            </label>

            {loanProductConfirmStep && selectedLoanProduct && loanProductDraft ? (
              <div className="space-y-3">
                <AlertCard
                  variant="caution"
                  title={`You are about to update ${selectedLoanProduct.name}. Interest and overdue behavior will change for future use of this product immediately after save.`}
                />
                <div className="grid gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-xs text-slate-600 md:grid-cols-2">
                  {productSummary.map((item) => (
                    <div key={item.label}>
                      <div className="font-semibold text-slate-700">{item.label}</div>
                      <div className="mt-1 text-red-500 line-through">{item.current}</div>
                      <div className="text-green-600">{item.next}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {loanProductFormError ? <AlertCard variant="danger" title={loanProductFormError} /> : null}

            {loanProductUpdateMutation.isError ? (
              <AlertCard
                variant="danger"
                title={
                  (loanProductUpdateMutation.error as Error)?.message ??
                  "Failed to update loan product configuration"
                }
              />
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" size="sm" onClick={closeLoanProductModal}>
                Cancel
              </Button>
              <Button
                variant={loanProductConfirmStep ? "danger" : "primary"}
                size="sm"
                disabled={!loanProductForm.reason.trim() || loanProductUpdateMutation.isPending}
                onClick={handleLoanProductSave}
              >
                {loanProductUpdateMutation.isPending
                  ? "Saving..."
                  : loanProductConfirmStep
                    ? "Confirm & Save"
                    : "Review Changes"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
